<template>
  <v-dialog v-model="dialog" max-width="720" persistent>
    <div class="home-wage-savings-modal">
      <LoadingOverlay :show="loading || detailLoading" text="불러오는 중..." />

      <!-- 요약 -->
      <template v-if="viewMode === 'summary'">
        <header class="home-wage-savings-modal__head">
          <h3 class="home-wage-savings-modal__title">인건비 절감 현황</h3>
          <button type="button" class="home-wage-savings-modal__close" aria-label="닫기" @click="close">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </header>

        <section class="home-wage-savings-modal__toolbar">
          <label class="home-wage-savings-modal__year-label">
            <span>조회 연도</span>
            <select v-model="viewYear" class="home-wage-savings-modal__year-select" @change="loadReport">
              <option v-for="y in availableYears" :key="y" :value="y">{{ y }}년</option>
            </select>
          </label>
        </section>

        <section class="home-wage-savings-modal__cards">
          <div class="home-wage-savings-modal__card">
            <span class="home-wage-savings-modal__card-label">{{ focusMonthLabel }} 절감</span>
            <span class="home-wage-savings-modal__card-value">{{ formatWageNumber(focusMonthSaving) }}<small>원</small></span>
          </div>
          <div class="home-wage-savings-modal__card">
            <span class="home-wage-savings-modal__card-label">{{ viewYear }}년 합계</span>
            <span class="home-wage-savings-modal__card-value">{{ formatWageNumber(yearTotal) }}<small>원</small></span>
          </div>
          <div class="home-wage-savings-modal__card home-wage-savings-modal__card--accent">
            <span class="home-wage-savings-modal__card-label">누적 절감</span>
            <span class="home-wage-savings-modal__card-value">{{ formatWageNumber(cumulativeTotal) }}<small>원</small></span>
          </div>
        </section>

        <section v-if="monthly.length" class="home-wage-savings-modal__chart-wrap">
          <div class="home-wage-savings-modal__chart-title">{{ viewYear }}년 월별 절감</div>
          <div class="home-wage-savings-modal__chart">
            <div
              v-for="item in monthly"
              :key="item.month"
              class="home-wage-savings-modal__bar-col"
            >
              <div class="home-wage-savings-modal__bar-area">
                <div
                  v-if="item.hasData"
                  class="home-wage-savings-modal__bar"
                  :class="{ 'home-wage-savings-modal__bar--focus': item.month === focusMonth && viewYear === focusYear }"
                  :style="{ height: barHeight(item.saving) }"
                  :title="`${item.month}월 ${formatWageNumber(item.saving)}원`"
                />
              </div>
              <span class="home-wage-savings-modal__bar-label">{{ item.month }}월</span>
            </div>
          </div>
        </section>

        <section v-else-if="!loading" class="home-wage-savings-modal__empty">
          {{ viewYear }}년 급여대장 데이터가 없습니다.
        </section>

        <div v-if="monthly.some((m) => m.hasData)" class="home-wage-savings-modal__table-wrap">
          <table class="home-wage-savings-modal__table">
            <thead>
              <tr>
                <th>월</th>
                <th>대상</th>
                <th>월 절감</th>
                <th>연간 누적</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in monthly"
                :key="`row-${item.month}`"
                :class="{ 'home-wage-savings-modal__row--empty': !item.hasData }"
              >
                <td>{{ item.month }}월</td>
                <td>{{ item.hasData ? `${item.employeeCount}명` : '-' }}</td>
                <td>{{ item.hasData ? `${formatWageNumber(item.saving)}원` : '-' }}</td>
                <td>{{ item.hasData ? `${formatWageNumber(item.cumulative)}원` : '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <footer class="home-wage-savings-modal__footer">
          <v-btn
            v-if="isAdmin"
            variant="flat"
            class="btn-edit home-wage-savings-modal__admin-btn"
            size="small"
            @click="openDetailView"
          >
            <v-icon size="14">mdi-account-search</v-icon>
            사원별 상세
          </v-btn>
          <v-spacer />
          <v-btn variant="text" class="btn-close" size="small" @click="close">닫기</v-btn>
        </footer>
      </template>

      <!-- 관리자 — 사원별 상세 -->
      <template v-else>
        <header class="home-wage-savings-modal__head">
          <div class="home-wage-savings-modal__head-left">
            <button type="button" class="home-wage-savings-modal__back" @click="viewMode = 'summary'">
              <v-icon size="16">mdi-arrow-left</v-icon>
              돌아가기
            </button>
            <h3 class="home-wage-savings-modal__title">사원별 비과세 절감</h3>
          </div>
          <button type="button" class="home-wage-savings-modal__close" aria-label="닫기" @click="close">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </header>

        <section class="home-wage-savings-modal__toolbar home-wage-savings-modal__toolbar--detail">
          <label class="home-wage-savings-modal__year-label">
            <span>연도</span>
            <select v-model="detailYear" class="home-wage-savings-modal__year-select" @change="loadEmployeeDetail">
              <option v-for="y in availableYears" :key="`d-${y}`" :value="y">{{ y }}년</option>
            </select>
          </label>
          <label class="home-wage-savings-modal__year-label">
            <span>월</span>
            <select v-model="detailMonth" class="home-wage-savings-modal__year-select" @change="loadEmployeeDetail">
              <option v-for="m in 12" :key="m" :value="m">{{ m }}월</option>
            </select>
          </label>
          <span class="home-wage-savings-modal__detail-badge">관리자 전용</span>
        </section>

        <div v-if="detailRows.length" class="home-wage-savings-modal__detail-summary">
          <span>{{ detailYear }}.{{ String(detailMonth).padStart(2, '0') }}</span>
          <span>{{ detailEmployeeCount }}명</span>
          <span class="home-wage-savings-modal__detail-summary-total">합계 {{ formatWageNumber(detailMonthTotal) }}원</span>
        </div>

        <div v-if="detailRows.length" class="home-wage-savings-modal__table-wrap home-wage-savings-modal__table-wrap--detail">
          <table class="home-wage-savings-modal__table home-wage-savings-modal__table--detail">
            <thead>
              <tr>
                <th>사원명</th>
                <th>직종</th>
                <th>비과세 항목</th>
                <th>비과세액</th>
                <th>절감액</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in detailRows" :key="`detail-${row.name}-${row.nontaxLabel}-${idx}`">
                <td
                  v-if="row.isFirstLine"
                  :rowspan="row.lineCount"
                  class="home-wage-savings-modal__cell-name"
                >
                  {{ row.name }}
                </td>
                <td
                  v-if="row.isFirstLine"
                  :rowspan="row.lineCount"
                  class="home-wage-savings-modal__cell-job"
                >
                  {{ row.job || '-' }}
                </td>
                <td class="home-wage-savings-modal__cell-label">{{ row.nontaxLabel }}</td>
                <td class="home-wage-savings-modal__cell-amount">{{ formatWageNumber(row.nontaxAmount) }}원</td>
                <td class="home-wage-savings-modal__cell-saving">{{ formatWageNumber(row.savingAmount) }}원</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="4">월 합계</td>
                <td>{{ formatWageNumber(detailMonthTotal) }}원</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <section v-else-if="!detailLoading" class="home-wage-savings-modal__empty">
          해당 월 비과세 절감 내역이 없습니다.
        </section>

        <footer class="home-wage-savings-modal__footer">
          <v-spacer />
          <v-btn variant="text" class="btn-close" size="small" @click="close">닫기</v-btn>
        </footer>
      </template>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import { formatWageNumber } from '@/composables/wage/useWageCalculator';
import {
  fetchHomeWageMonthSaving,
  fetchHomeWageNontaxSavingsReport,
  fetchHomeWageEmployeeSavingsDetail,
  flattenEmployeeSavingsDetails,
  buildJoinDateMapFromEmployees,
  type HomeWageNontaxSavingsMonthRow,
  type HomeWageEmployeeSavingsDetailRow,
} from '@/composables/wage/useHomeWageNontaxSavingsReport';

const props = defineProps<{
  mCode: string;
  bizGubun: string;
  focusYear: number;
  focusMonth: number;
  employees?: Array<Record<string, unknown>>;
  /** role_level 60+ 관리자만 사원별 상세 노출 */
  isAdmin?: boolean;
}>();

const emit = defineEmits<{
  'update:monthSaving': [value: number | null];
  'update:loadingMonthSaving': [value: boolean];
}>();

const dialog = ref(false);
const loading = ref(false);
const detailLoading = ref(false);
const viewMode = ref<'summary' | 'detail'>('summary');
const viewYear = ref(props.focusYear);
const detailYear = ref(props.focusYear);
const detailMonth = ref(props.focusMonth);
const focusMonthSaving = ref(0);
const yearTotal = ref(0);
const cumulativeTotal = ref(0);
const monthly = ref<HomeWageNontaxSavingsMonthRow[]>([]);
const detailRows = ref<HomeWageEmployeeSavingsDetailRow[]>([]);

const focusYear = computed(() => props.focusYear);
const focusMonth = computed(() => props.focusMonth);
const isAdmin = computed(() => props.isAdmin === true);

const availableYears = computed(() => {
  const current = new Date().getFullYear();
  const years: number[] = [];
  for (let y = current; y >= 2020; y -= 1) years.push(y);
  return years;
});

const focusMonthLabel = computed(() => {
  if (viewYear.value === props.focusYear) {
    return `${props.focusYear}.${String(props.focusMonth).padStart(2, '0')}`;
  }
  return `${viewYear.value}년`;
});

const detailEmployeeCount = computed(() => {
  const names = new Set(detailRows.value.filter((r) => r.isFirstLine).map((r) => r.name));
  return names.size;
});

const detailMonthTotal = computed(() =>
  detailRows.value.filter((r) => r.isFirstLine).reduce((sum, r) => sum + r.employeeTotalSaving, 0)
);

const maxBarSaving = computed(() =>
  Math.max(...monthly.value.filter((m) => m.hasData).map((m) => m.saving), 1)
);

const barHeight = (saving: number) => {
  const pct = Math.max(4, Math.round((saving / maxBarSaving.value) * 100));
  return `${pct}%`;
};

const joinDateByKey = computed(() => buildJoinDateMapFromEmployees(props.employees ?? []));

const loadFocusMonthSaving = async () => {
  if (!props.mCode) {
    focusMonthSaving.value = 0;
    emit('update:monthSaving', null);
    return;
  }
  emit('update:loadingMonthSaving', true);
  try {
    const result = await fetchHomeWageMonthSaving({
      mCode: props.mCode,
      bizGubun: props.bizGubun || '',
      year: props.focusYear,
      month: props.focusMonth,
      joinDateByKey: joinDateByKey.value,
    });
    focusMonthSaving.value = result.saving;
    emit('update:monthSaving', result.hasData ? result.saving : 0);
  } catch (error: unknown) {
    const message =
      (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
      (error as Error)?.message ||
      '알 수 없는 오류';
    alert(`당월 절감액 조회 실패: ${message}`);
    emit('update:monthSaving', null);
  } finally {
    emit('update:loadingMonthSaving', false);
  }
};

const loadReport = async () => {
  if (!props.mCode) return;
  loading.value = true;
  try {
    const result = await fetchHomeWageNontaxSavingsReport({
      mCode: props.mCode,
      bizGubun: props.bizGubun || '',
      year: viewYear.value,
      joinDateByKey: joinDateByKey.value,
    });
    monthly.value = result.yearReport.monthly;
    yearTotal.value = result.yearReport.yearTotal;
    cumulativeTotal.value = result.cumulativeTotal;
  } catch (error: unknown) {
    const message =
      (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
      (error as Error)?.message ||
      '알 수 없는 오류';
    alert(`절감 현황 조회 실패: ${message}`);
    monthly.value = [];
    yearTotal.value = 0;
    cumulativeTotal.value = 0;
  } finally {
    loading.value = false;
  }
};

const loadEmployeeDetail = async () => {
  if (!props.mCode || !isAdmin.value) return;
  detailLoading.value = true;
  try {
    const details = await fetchHomeWageEmployeeSavingsDetail({
      mCode: props.mCode,
      bizGubun: props.bizGubun || '',
      year: detailYear.value,
      month: detailMonth.value,
      joinDateByKey: joinDateByKey.value,
    });
    detailRows.value = flattenEmployeeSavingsDetails(details);
  } catch (error: unknown) {
    const message =
      (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
      (error as Error)?.message ||
      '알 수 없는 오류';
    alert(`사원별 상세 조회 실패: ${message}`);
    detailRows.value = [];
  } finally {
    detailLoading.value = false;
  }
};

const openDetailView = async () => {
  if (!isAdmin.value) return;
  detailYear.value = viewYear.value;
  detailMonth.value =
    viewYear.value === props.focusYear ? props.focusMonth : 1;
  viewMode.value = 'detail';
  await loadEmployeeDetail();
};

const open = async () => {
  viewYear.value = props.focusYear;
  viewMode.value = 'summary';
  dialog.value = true;
  await loadReport();
};

const close = () => {
  dialog.value = false;
  viewMode.value = 'summary';
};

watch(
  () => [props.mCode, props.bizGubun, props.focusYear, props.focusMonth, props.employees?.length],
  () => {
    if (props.mCode) loadFocusMonthSaving();
  },
  { immediate: true }
);

defineExpose({ open, loadFocusMonthSaving, focusMonthSaving });
</script>

<style lang="scss">
@import '@/assets/styles/variables';
@import '@/assets/styles/components/home-wage-nontax-savings';
</style>
